import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { UiField, UiSection, UiSectionLayout } from '../types/uiSchema';

const SECTION_LAYOUTS: UiSectionLayout[] = ['single_column', 'two_column', 'grid', 'twelve_column'];

type SchemaField = {
  id: string;
  label?: string;
  name?: string;
  type?: string;
  required?: boolean;
  allowed_values?: string[];
  default_value?: unknown;
  accept?: string;
};

const FIELD_TYPES = [
  'text', 'textarea', 'number', 'date',
  'single_select', 'multi_select', 'radio',
  'checkbox', 'boolean', 'email', 'phone',
  'link', 'address', 'currency', 'ref_field', 'file', 'space',
] as const;

export default function SimplePropertiesPanel(props: {
  selectionKind: 'none' | 'field' | 'section';
  sectionStableId: string;
  sectionTitle: string;
  uiSection: Partial<UiSection> | null;
  schemaField: SchemaField | null;
  uiField: UiField | null;
  onPatchSchemaField: (patch: Partial<SchemaField>) => void;
  onPatchUiField: (patch: Partial<UiField>) => void;
  onPatchSection: (patch: Partial<UiSection>) => void;
}) {
  const {
    selectionKind,
    sectionStableId,
    sectionTitle,
    uiSection,
    schemaField,
    uiField,
    onPatchSchemaField,
    onPatchUiField,
    onPatchSection,
  } = props;
  const options = Array.isArray(schemaField?.allowed_values) ? schemaField.allowed_values : [];
  const showHint = Boolean((uiField?.help_text || '').trim());

  if (selectionKind === 'none') {
    return (
      <Box sx={{ px: 1, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Select a field (or the section chip) to edit properties.
        </Typography>
      </Box>
    );
  }

  if (selectionKind === 'section') {
    const displayTitle = String(uiSection?.title ?? sectionTitle ?? '').trim() || sectionTitle;
    return (
      <Stack spacing={1.5} sx={{ px: 1, pb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Section
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          Id: <code>{sectionStableId || '—'}</code>
        </Typography>
        <TextField
          size="small"
          label="Title"
          fullWidth
          value={displayTitle}
          onChange={(e) => onPatchSection({ title: e.target.value })}
          helperText="Shown in the form header and runtime."
        />
        <TextField
          select
          size="small"
          label="Layout mode"
          fullWidth
          value={(uiSection?.layout as UiSectionLayout) || 'two_column'}
          onChange={(e) => onPatchSection({ layout: e.target.value as UiSectionLayout })}
        >
          {SECTION_LAYOUTS.map((l) => (
            <MenuItem key={l} value={l}>{l}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Density"
          fullWidth
          value={uiSection?.density || 'comfortable'}
          onChange={(e) => onPatchSection({ density: e.target.value as 'comfortable' | 'compact' })}
        >
          <MenuItem value="comfortable">Comfortable</MenuItem>
          <MenuItem value="compact">Compact</MenuItem>
        </TextField>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={Boolean(uiSection?.locked)}
              onChange={(_, v) => onPatchSection({ locked: v })}
            />
          )}
          label="Lock section auto-layout"
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25} sx={{ px: 1, pb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Field
      </Typography>
      <TextField size="small" label="Field Name" value={String(schemaField?.id || '')} fullWidth disabled />
      <TextField
        size="small"
        label="Label"
        value={String(schemaField?.label ?? schemaField?.name ?? '')}
        onChange={(e) => onPatchSchemaField({ label: e.target.value })}
        fullWidth
      />
      <TextField
        select
        size="small"
        label="Type"
        value={String(schemaField?.type || 'text')}
        onChange={(e) => onPatchSchemaField({ type: e.target.value })}
        fullWidth
      >
        {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      <FormControlLabel
        control={(
          <Switch
            size="small"
            checked={Boolean(schemaField?.required)}
            onChange={(_, v) => onPatchSchemaField({ required: v })}
          />
        )}
        label="Required"
      />
      {(schemaField?.type === 'single_select' || schemaField?.type === 'multi_select') && (
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary">Items</Typography>
          {options.map((opt, idx) => (
            <Stack key={`${opt}-${idx}`} direction="row" spacing={0.75} alignItems="center">
              <TextField
                size="small"
                value={opt}
                onChange={(e) => {
                  const next = [...options];
                  next[idx] = e.target.value;
                  onPatchSchemaField({ allowed_values: next.map((x) => x.trim()).filter(Boolean) });
                }}
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => onPatchSchemaField({ allowed_values: options.filter((_, i) => i !== idx) })}
                aria-label="Delete option"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => onPatchSchemaField({ allowed_values: [...options, `Option ${options.length + 1}`] })}
          >
            Add item
          </Button>
        </Stack>
      )}
      {(schemaField?.type === 'single_select' || schemaField?.type === 'multi_select' || schemaField?.type === 'radio') && (
        <TextField
          select
          size="small"
          label="Dropdown type"
          value={schemaField?.type === 'multi_select' ? 'multi' : 'single'}
          onChange={(e) => onPatchSchemaField({ type: e.target.value === 'multi' ? 'multi_select' : 'single_select' })}
          fullWidth
        >
          <MenuItem value="single">Single choice</MenuItem>
          <MenuItem value="multi">Multi choice</MenuItem>
        </TextField>
      )}
      {(schemaField?.type === 'single_select' || schemaField?.type === 'multi_select' || schemaField?.type === 'radio') && (
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={schemaField?.default_value !== undefined && schemaField?.default_value !== null && schemaField?.default_value !== ''}
              onChange={(_, v) => onPatchSchemaField({ default_value: v ? (options[0] || '') : undefined })}
            />
          )}
          label="Default value"
        />
      )}
      {(schemaField?.type === 'single_select' || schemaField?.type === 'radio') && (
        <TextField
          select
          size="small"
          label="Select item"
          value={String(schemaField?.default_value ?? '')}
          onChange={(e) => onPatchSchemaField({ default_value: e.target.value })}
          fullWidth
        >
          <MenuItem value="">(none)</MenuItem>
          {options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>
      )}
      {schemaField?.type === 'multi_select' && (
        <TextField
          size="small"
          label="Default items (comma-separated)"
          value={Array.isArray(schemaField?.default_value) ? schemaField.default_value.map(String).join(', ') : ''}
          onChange={(e) => onPatchSchemaField({ default_value: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          fullWidth
        />
      )}
      {schemaField?.type === 'file' && (
        <TextField
          size="small"
          label="Accepted formats"
          value={String(schemaField?.accept ?? '.pdf,.jpg,.jpeg,.png')}
          onChange={(e) => onPatchSchemaField({ accept: e.target.value })}
          helperText="Example: .pdf,.jpg,.png"
          fullWidth
        />
      )}
      <Divider />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        Display hints
      </Typography>
      <TextField
        size="small"
        label="Placeholder"
        value={String(uiField?.placeholder ?? '')}
        onChange={(e) => onPatchUiField({ placeholder: e.target.value })}
        fullWidth
      />
      <TextField
        size="small"
        label="Help text"
        value={String(uiField?.help_text ?? '')}
        onChange={(e) => onPatchUiField({ help_text: e.target.value })}
        fullWidth
        multiline
        minRows={2}
        disabled={!showHint}
      />
      <FormControlLabel
        control={(
          <Switch
            size="small"
            checked={showHint}
            onChange={(_, v) => onPatchUiField({ help_text: v ? (uiField?.help_text || 'Helpful guidance') : '' })}
          />
        )}
        label="Show hint"
      />
    </Stack>
  );
}

